import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronDown, Plus, Trash2 } from "lucide-react";
import {
  CMS_HERO_FALLBACK_DEFAULTS,
  CMS_HERO_LINK_LABELS,
  CMS_HERO_LINK_TYPES,
  CMS_INTERNAL_PATH_PRESETS,
  type CmsHeroFallback,
  type CmsHeroLinkType,
} from "@gaole/shared";
import { api } from "../../lib/api";
import { toast } from "../../stores/toast";
import { useAuth } from "../../stores/auth";
import { useServerDraft } from "../../hooks/useServerDraft";
import { showQueryLoader } from "../../lib/query-utils";
import { ImageUploadField } from "../../components/ImageUploadField";
import { Button, Input, PageLoader, SearchSelect, SettingsCard, Textarea, Toggle } from "../../components/ui";

interface HeroSlideDraft {
  clientId: string;
  sortOrder: number;
  enabled: boolean;
  imageUrl: string;
  title: string;
  subtitle: string;
  linkType: CmsHeroLinkType;
  cmsPageId: string;
  externalUrl: string;
  internalPath: string;
}

interface HeroSlideRow {
  id: string;
  sortOrder: number;
  enabled: boolean;
  imageUrl: string;
  title: string | null;
  subtitle: string | null;
  linkType: CmsHeroLinkType;
  cmsPageId: string | null;
  externalUrl: string | null;
  internalPath: string | null;
}

interface CmsPageOption {
  id: string;
  title: string;
  slug: string;
  status: string;
}

function mapHeroSlides(items: HeroSlideRow[]): HeroSlideDraft[] {
  return items.map((s, index) => ({
    clientId: s.id,
    sortOrder: index,
    enabled: s.enabled,
    imageUrl: s.imageUrl,
    title: s.title ?? "",
    subtitle: s.subtitle ?? "",
    linkType: s.linkType,
    cmsPageId: s.cmsPageId ?? "",
    externalUrl: s.externalUrl ?? "",
    internalPath: s.internalPath ?? "/shop",
  }));
}

function newSlide(order: number): HeroSlideDraft {
  return {
    clientId: `new-${Math.random().toString(36).slice(2, 9)}`,
    sortOrder: order,
    enabled: true,
    imageUrl: "",
    title: "",
    subtitle: "",
    linkType: "NONE",
    cmsPageId: "",
    externalUrl: "",
    internalPath: "/shop",
  };
}

function SortableHeroRow({
  slide,
  pageOptions,
  expanded,
  onToggleExpand,
  onChange,
  onRemove,
}: {
  slide: HeroSlideDraft;
  pageOptions: { value: string; label: string; hint?: string }[];
  expanded: boolean;
  onToggleExpand: () => void;
  onChange: (next: HeroSlideDraft) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slide.clientId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const summaryParts = [
    slide.title.trim() || "Untitled",
    !slide.enabled ? "Disabled" : null,
    slide.linkType !== "NONE" ? CMS_HERO_LINK_LABELS[slide.linkType] : null,
  ].filter(Boolean);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`overflow-hidden rounded-xl border border-line bg-white/[0.02] ${expanded ? "p-4" : "px-3 py-2"}`}
    >
      <div className={`flex items-center gap-2 ${expanded ? "mb-3" : ""}`}>
        <button
          type="button"
          className="cursor-grab text-white/40 hover:text-white/70"
          aria-label={`Reorder slide ${slide.sortOrder + 1}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggleExpand}
          aria-expanded={expanded}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 text-white/45 transition-transform ${expanded ? "" : "-rotate-90"}`}
            aria-hidden
          />
          <span className="min-w-0 truncate text-sm font-medium">Slide {slide.sortOrder + 1}</span>
          {!expanded && (
            <span className="min-w-0 truncate text-xs text-white/45">{summaryParts.join(" · ")}</span>
          )}
        </button>
        {slide.imageUrl && !expanded ? (
          <img
            src={slide.imageUrl}
            alt=""
            className="h-8 w-12 shrink-0 rounded object-cover ring-1 ring-white/10"
          />
        ) : null}
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Toggle label="Enabled" checked={slide.enabled} onChange={(v) => onChange({ ...slide, enabled: v })} />
        </div>
        <button
          type="button"
          className="shrink-0 text-white/40 hover:text-red-300"
          aria-label={`Remove slide ${slide.sortOrder + 1}`}
          onClick={onRemove}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {expanded ? (
        <div className="space-y-3 border-t border-line pt-3">
          <ImageUploadField
            label="Hero image"
            hint="Wide image recommended (16:9). Shown full-width on the storefront home carousel."
            max={1}
            value={slide.imageUrl ? [slide.imageUrl] : []}
            onChange={(urls) => onChange({ ...slide, imageUrl: urls[0] ?? "" })}
          />
          <Input label="Title" value={slide.title} onChange={(e) => onChange({ ...slide, title: e.target.value })} />
          <Input
            label="Subtitle"
            value={slide.subtitle}
            onChange={(e) => onChange({ ...slide, subtitle: e.target.value })}
          />
          <SearchSelect
            label="Link type"
            options={CMS_HERO_LINK_TYPES.map((t) => ({ value: t, label: CMS_HERO_LINK_LABELS[t] }))}
            value={slide.linkType}
            onChange={(v) => onChange({ ...slide, linkType: v as CmsHeroLinkType })}
          />
          {slide.linkType === "ARTICLE" && (
            <SearchSelect
              label="CMS page"
              placeholder="Select a page…"
              options={pageOptions}
              value={slide.cmsPageId}
              onChange={(v) => onChange({ ...slide, cmsPageId: v })}
            />
          )}
          {slide.linkType === "EXTERNAL" && (
            <Input
              label="External URL"
              value={slide.externalUrl}
              onChange={(e) => onChange({ ...slide, externalUrl: e.target.value })}
              placeholder="https://…"
            />
          )}
          {slide.linkType === "INTERNAL" && (
            <>
              <SearchSelect
                label="Quick route"
                options={CMS_INTERNAL_PATH_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                value={slide.internalPath}
                onChange={(v) => onChange({ ...slide, internalPath: v })}
              />
              <Input
                label="Custom path"
                value={slide.internalPath}
                onChange={(e) => onChange({ ...slide, internalPath: e.target.value })}
                hint="Must start with / and match an allowed storefront route."
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function CmsHero() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canWrite = can("cms.write");

  const heroQuery = useQuery({
    queryKey: ["cms-hero"],
    queryFn: () =>
      api<{ items: HeroSlideRow[]; fallback: CmsHeroFallback }>("/cms/hero").then((r) => r),
  });

  const pagesQuery = useQuery({
    queryKey: ["cms-pages-options"],
    queryFn: () => api<{ items: CmsPageOption[] }>("/cms/pages?pageSize=100").then((r) => r.items),
  });

  const serverSlides = useMemo(
    () => (heroQuery.data ? mapHeroSlides(heroQuery.data.items) : null),
    [heroQuery.data],
  );
  const serverFallback = heroQuery.data?.fallback ?? CMS_HERO_FALLBACK_DEFAULTS;

  const {
    value: slides,
    setValue: setSlides,
    reset: resetSlides,
    isDirty: slidesDirty,
  } = useServerDraft(serverSlides);
  const {
    value: fallback,
    setValue: setFallback,
    reset: resetFallback,
    isDirty: fallbackDirty,
  } = useServerDraft(serverFallback);

  const resolvedSlides = slides ?? [];
  const resolvedFallback = fallback ?? CMS_HERO_FALLBACK_DEFAULTS;
  const dirty = slidesDirty || fallbackDirty;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const saveMut = useMutation({
    mutationFn: () =>
      api("/cms/hero", {
        method: "PUT",
        body: {
          slides: resolvedSlides.map((s, index) => ({
            sortOrder: index,
            enabled: s.enabled,
            imageUrl: s.imageUrl,
            title: s.title || null,
            subtitle: s.subtitle || null,
            linkType: s.linkType,
            cmsPageId: s.linkType === "ARTICLE" ? s.cmsPageId || null : null,
            externalUrl: s.linkType === "EXTERNAL" ? s.externalUrl || null : null,
            internalPath: s.linkType === "INTERNAL" ? s.internalPath || null : null,
          })),
          fallback: resolvedFallback,
        },
      }),
    onSuccess: () => {
      toast("success", "Hero saved");
      resetSlides();
      resetFallback();
      collapseAll();
      void queryClient.invalidateQueries({ queryKey: ["cms-hero"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlides((items) => {
      const oldIndex = items.findIndex((s) => s.clientId === active.id);
      const newIndex = items.findIndex((s) => s.clientId === over.id);
      return arrayMove(items, oldIndex, newIndex).map((s, index) => ({ ...s, sortOrder: index }));
    });
  }, [setSlides]);

  if (showQueryLoader(heroQuery)) return <PageLoader />;

  const pageOptions = (pagesQuery.data ?? [])
    .filter((p) => p.status === "PUBLISHED")
    .map((p) => ({ value: p.id, label: p.title, hint: `/pages/${p.slug}` }));

  return (
    <div className="page-scroll mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">Slider</h1>
        <p className="mt-0.5 text-xs text-muted">
          Home carousel slides and the default banner copy when no slides are active.
        </p>
      </div>
      <SettingsCard
        title="Default banner"
        description="Shown on the storefront home page when CMS is off, there are no slides, or every slide is disabled."
      >
        <div className="space-y-3">
          <Input
            label="Eyebrow"
            hint="Small label above the headline — e.g. store name"
            value={resolvedFallback.eyebrow}
            onChange={(e) => {
              setFallback((prev) => ({ ...prev, eyebrow: e.target.value }));
            }}
          />
          <Input
            label="Headline"
            hint='The word "collectibles" is highlighted automatically when present.'
            value={resolvedFallback.headline}
            onChange={(e) => {
              setFallback((prev) => ({ ...prev, headline: e.target.value }));
            }}
          />
          <Textarea
            label="Description"
            rows={3}
            value={resolvedFallback.description}
            onChange={(e) => {
              setFallback((prev) => ({ ...prev, description: e.target.value }));
            }}
          />
        </div>
      </SettingsCard>
      <SettingsCard
        title="Carousel slides"
        description="Click a slide to expand. Collapse slides to reorder with the drag handle."
        headerActions={
          canWrite ? (
            <Button
              size="sm"
              variant="outline"
              className="inline-flex items-center gap-1.5"
              onClick={() => {
                const slide = newSlide(resolvedSlides.length);
                setSlides((items) => [...items, slide]);
                setExpandedIds(new Set([slide.clientId]));
              }}
            >
              <Plus size={14} aria-hidden />
              Add slide
            </Button>
          ) : undefined
        }
        footer={
          canWrite ? (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!dirty}
                onClick={() => {
                  collapseAll();
                  resetSlides();
                  resetFallback();
                }}
              >
                Cancel
              </Button>
              <Button size="sm" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()}>
                Save
              </Button>
            </div>
          ) : undefined
        }
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={resolvedSlides.map((s) => s.clientId)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {resolvedSlides.map((slide) => (
                <SortableHeroRow
                  key={slide.clientId}
                  slide={slide}
                  pageOptions={pageOptions}
                  expanded={expandedIds.has(slide.clientId)}
                  onToggleExpand={() => toggleExpanded(slide.clientId)}
                  onChange={(next) => {
                    setSlides((items) => items.map((s) => (s.clientId === slide.clientId ? next : s)));
                  }}
                  onRemove={() => {
                    setSlides((items) => items.filter((s) => s.clientId !== slide.clientId).map((s, i) => ({ ...s, sortOrder: i })));
                    setExpandedIds((prev) => {
                      const next = new Set(prev);
                      next.delete(slide.clientId);
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </SettingsCard>
    </div>
  );
}
